import { shiftMandatoryValidations, unallocationModeOptions } from 'constants/allocations';
import { getAllocationShiftTimingsPayload, validateMandatoryShiftTimings } from 'helper/shift-timings/helper-function';
import { extractApiErrorMessage } from 'helper/util';
import { getDefaultTaskCode } from 'lib/util';
import moment from 'moment';
import { bulkUnAllocation, createAllocationV2, editTenure, getTotalHoursForAllocateMeAnyway } from 'services/allocation.service';
import dialogActionStore from 'store/dialogActionStore';
import useGlobalToastStore, { useGlobalAlertStore } from 'store/globalToastStore';

const useAllocationHooks = () => {
    const { setToastData } = useGlobalToastStore();
    const { setDialogActionData } = dialogActionStore();
    const { setAlertData } = useGlobalAlertStore();

    const getAllocationPayload = ({
        al, pr
    }) => {
        const defaultSwiftTaskCode = getDefaultTaskCode();
        const task_code = (al.taskCode && (typeof al.taskCode === "object") ? al.taskCode.value : al.taskCode)
        const { isSpecialProject, projectType } = pr;
        const startDate = moment(al.startDate).startOf('day').format();
        const endDate = moment(al.endDate).startOf('day').format();
        const weeklyHours = (Number(al.hoursPerWeek) || al.weeklyHours);

        const payload = {
            hasError: al.hasError,
            id: al.id,
            startDate,
            endDate,
            weeklyHours,
            projectId: pr.id,
            selectedDemandId: al.demandId,
            projectType: `${projectType}-project`,
            allocateAnyways: !isSpecialProject,
            nonBillableProjectData: al.isNewNonBillableProject ? al.nonBillableProjectData : null,
            includeSaturday: al.include ? al.include[0].value : false,
            includeSunday: al.include ? al.include[1].value : false,
            taskCode: task_code?.length ? task_code : defaultSwiftTaskCode.value,
            taskDescription: (al.taskCode && (typeof al.taskCode === 'object') ? al.taskCode.label : al.taskDescription) ?? defaultSwiftTaskCode.label,
            adjustedUtilizations: al.adjustedUtilizations || false,
            resourceId: al.resourceId,
            budgetExceedConsent: al.budgetExceedConsent || false,
            isContinuityResource: al.isContinuityResource,
            continuityReason: al.isContinuityResource ? al.continuityReason?.value : null,
            continuityReasonComment: al.isContinuityResource ? al.continuityReasonComment : '',
            cloneDemand: al.demand?.demandStatus === 'Fulfilled' || al.demand?.demandStatus === 'Partially Fulfilled',
            allocationShiftTimings: getAllocationShiftTimingsPayload(al.allocationShiftTimings || {}),
            wbsL4Code: al.wbsL4Code ? typeof al.wbsL4Code === 'object' ? al.wbsL4Code?.value : al.wbsL4Code : null,
            getTotalHoursForAllocateMeAnyway: getTotalHoursForAllocateMeAnyway(
                startDate,
                endDate,
                weeklyHours
            )
        };

        return payload;
    }

    const getPayloadAfterDrawerEdit = (data) => {

        return {
            startDate: new Date(data.startDate).toUTCString(),
            endDate: new Date(data.endDate).toUTCString(),
            weeklyHours: parseFloat(data.hoursPerWeek),
            includeSaturday: data.include && data.include[0]?.value,
            includeSunday: data.include && data.include[1]?.value,
            adjustedUtilizations: data?.adjustedUtilizations,
            isContinuityResource: data?.isContinuityResource,
            continuityReason: data?.isContinuityResource ? data?.continuityReason?.value : null,
            continuityReasonComment: data?.isContinuityResource ? data?.continuityReasonComment : '',
            isBudgetExceed: data?.isBudgetExceed || false,
            allocationShiftTimings: getAllocationShiftTimingsPayload(data?.allocationShiftTimings),
            wbsL4Code: data?.wbsL4Code ? typeof data?.wbsL4Code === 'object' ? data?.wbsL4Code?.value : data?.wbsL4Code : null
        }
    }

    const createAllocation = async (body, fromAvlReport = 'yes') => {
        const res = await createAllocationV2(Object.assign(body, { fromAvlReport }));

        if (res.status) {
            return {
                ...res,
                message: 'Successfully created allocation.'
            }
        }
        return res;
    };

    const isValidShiftTiming = (data) => {
        const { demand = {} } = data;

        const { project = {} } = demand;
        const { isSpecialProject } = project;
        if (!isSpecialProject) return true

        return validateMandatoryShiftTimings({
            startDate: data?.startDate,
            endDate: data?.endDate,
            shiftTimings: data?.allocationShiftTimings
        })
    }

    const onEditAllocation = async (data, row) => {
        const { isNew, isResized } = row;

        if (!isValidShiftTiming(data) && !isResized) {
            setToastData({
                show: true,
                type: 'error',
                message: shiftMandatoryValidations.errorMessage
            });
            return { success: false };
        }
        if (isNew || isResized) {
            return { success: true, data: Object.assign(data, { isNew, isResized }) };
        }

        const payload = getPayloadAfterDrawerEdit(data);

        const updateRes = await editTenure(row.id, payload);

        if (updateRes.status) {
            if (updateRes.entity.message === 'Conflict Error') {
                setDialogActionData({ conflictData: updateRes?.entity?.data });
                // setShowConflict({ show: true, noOfError: 'some', data: updateRes?.entity?.data });
                return { success: false };
            }

            if (updateRes.entity.message === 'Expected Budget Exceeding') {
                // setAlertModalData('allocate-budget-exceeded-alert')
                return { success: false };
            }

            if (updateRes?.message === "Successfully updated tenure") {
                setToastData({
                    show: true,
                    type: 'success',
                    message: `The tenure for ${row?.name} has been successfully updated for ${row?.project?.projectName} from ${moment(data?.startDate).format('MM/DD/YYYY')} to ${moment(data?.endDate).format('MM/DD/YYYY')} for ${data?.hoursPerWeek} Hrs/Week!`
                });
            } else {
                setToastData({ show: true, message: updateRes?.message, type: 'success' });
            }
            setTimeout(() => {
                setToastData({});
            }, 2000);
            return { success: true, data };
        } else if (updateRes?.error?.response?.status === 400) {
            setToastData({ show: true, message: updateRes?.error?.response?.data?.message, type: 'error' });
        }
        else {
            setToastData({ show: true, message: 'Something went wrong', type: 'error' });
        }
        setTimeout(() => {
            setToastData({});
        }, 3000);
        return { success: false, validationErrors: {} };
    };

    const onUnallocateConfirm = ({
        reason, resourceId,
        projectId, onSuccess
    }) => {
        const {
            saveLabel,
            cancelLabel,
            title,
            description: message,
        } = unallocationModeOptions;

        setAlertData({
            show: true,
            saveLabel,
            cancelLabel,
            title,
            message,
            onSave: async () => {
                const updateRes = await bulkUnAllocation(reason, [{ resourceId, allocationId: projectId }]);

                if (updateRes.status) {
                    setAlertData({ show: false });

                    setToastData({
                        show: true,
                        type: 'success',
                        message: updateRes?.message
                    });
                } else if (updateRes.error?.response?.status === 400) {
                    const responseData = updateRes?.error?.response?.data || {};
                    if (responseData.error === 'Date Validation Error') {
                        setToastData({
                            show: true,
                            message: 'Unallocation for past dates can only be done by your Resource Manager. Please request your RM to unallocate ONLY if the practitioner has never worked on this demand. However, if the practitioner has worked for a certain duration, the dates can be modified to reflect accurate schedules.',
                            type: 'error'
                        });
                    } else {
                        const message = extractApiErrorMessage(responseData, 'Failed to unallocate employee from this demand');
                        setToastData({
                            show: true,
                            message,
                            type: 'error'
                        });
                    }
                } else {
                    setToastData({ show: true, message: 'Failed to unallocate employee from this demand', type: 'error' });
                }
                setTimeout(() => {
                    setToastData({ show: false });
                }, 3000);
                if (onSuccess) {
                    onSuccess({ resourceId, projectId });
                }
            }
        });
    }

    const onUnallocate = ({ resourceId, projectId, onSuccess }) => {
        setDialogActionData({
            dailogType: 'take-reason',
            dialogActionData: { resourceId, projectId, onSuccess }
        });
    }

    return {
        onEditAllocation, createAllocation, onUnallocate, onUnallocateConfirm, isValidShiftTiming,
        getPayloadAfterDrawerEdit, getAllocationPayload
    };
};

export default useAllocationHooks;